import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { translateMessage } from '../../message-translation/actions/translate-message';
import {
  messageTranslationForMessageIdSelector,
  messageTranslationsStateForMessageIdSelector,
} from '../../message-translation/message-translation-selectors';

type UseSentMessageTranslationProps = {
  /**
   * The message content to be translated
   */
  messageBody: string;
  /**
   * Optional subject to translate
   */
  subject?: string;
  /**
   * For a message to be translated. This hook will store the result in Redux
   */
  messageId: number | undefined;
  /**
   * Language code of the language to translate into
   */
  languageCode?: string | null;
};

type UseSentMessageTranslationResponse = {
  /**
   * Boolean to disable translate button if message body length exceeds the maximum allowed
   * character limit
   */
  disableTranslateButton: boolean;
  /**
   * Message to display to user for why the button is disabled.
   */
  disableTranslateButtonMessage: string | null;
  /**
   * Whether or not the consumer should render the translation
   */
  shouldShowTranslation: boolean;
  /**
   * Sets shouldShowTranslation false
   */
  hideTranslation: () => void;
  /**
   * Translated message body
   */
  translatedMessageBody?: string;
  /**
   * Translated subject, if subject is provided
   */
  translatedSubject?: string;
  /**
   * Commences a message translation and sets shouldShowTranslation true
   */
  onTranslateMessageClick: () => void;
  /**
   * Whether or not a translation is actively streaming
   */
  loading: boolean;
};

export const MAX_TRANSLATE_CHARACTER_LIMIT = 15000;
export const TRANSLATE_CHARACTER_LIMIT_EXCEEDED_MESSAGE =
  'Message must be less than 15000 characters to use translation.';

/**
 * Hook that exposes message translations for sent messages associated with a
 * messageId. Exposes fields for use in a TranslateMessageButton
 */
export const useSentMessageTranslation = ({
  messageBody,
  subject,
  messageId,
  languageCode,
}: UseSentMessageTranslationProps): UseSentMessageTranslationResponse => {
  const dispatch = useDispatch();
  const messageTranslations = useSelector((state: any) =>
    messageId ? messageTranslationForMessageIdSelector(state, messageId) : undefined,
  );
  const messageTranslationState = useSelector((state: any) =>
    messageId ? messageTranslationsStateForMessageIdSelector(state, messageId) : undefined,
  );

  const [shouldShowTranslation, setShouldShowTranslation] = useState(false);
  const [translatedMessageBody, setTranslatedMessageBody] = useState<string | undefined>();
  const [translatedSubject, setTranslatedSubject] = useState<string | undefined>();
  const loading = messageTranslationState?.loading || false;

  const disableTranslateButton = (messageBody || '').length > MAX_TRANSLATE_CHARACTER_LIMIT;
  const disableTranslateButtonMessage = disableTranslateButton
    ? TRANSLATE_CHARACTER_LIMIT_EXCEEDED_MESSAGE
    : null;

  // we currently only support translating sent messages into English
  const languageToTranslateTo = messageId ? 'en' : languageCode;

  const prevMessageBody = useRef<string | undefined>();
  const prevSubject = useRef<string | undefined>();

  // This useEffect is only triggered when streaming with a messageId
  // to set change events from redux and send them to the parent
  useEffect(() => {
    if (
      !!messageId &&
      messageTranslations &&
      languageToTranslateTo &&
      messageTranslations[languageToTranslateTo]
    ) {
      const { subject: newSubject, messageBody: newMessageBody } =
        messageTranslations[languageToTranslateTo];

      if (prevMessageBody.current !== newMessageBody) {
        prevMessageBody.current = newMessageBody;
        setTranslatedMessageBody(newMessageBody);
      }

      if (prevSubject.current !== newSubject) {
        prevSubject.current = newSubject;
        setTranslatedSubject(newSubject);
      }
    }
  }, [languageToTranslateTo, messageId, messageTranslations]);

  const onTranslateMessageClick = () => {
    if (!languageToTranslateTo || !messageId) {
      return;
    }

    setShouldShowTranslation(true);
    if (
      messageTranslations &&
      languageToTranslateTo &&
      messageTranslations[languageToTranslateTo]
    ) {
      return;
    }
    dispatch(
      translateMessage({
        languageCode: languageToTranslateTo,
        messageBody,
        subject,
        messageId,
      }),
    );
  };

  const hideTranslation = () => {
    setShouldShowTranslation(false);
  };

  return {
    disableTranslateButton,
    disableTranslateButtonMessage,
    shouldShowTranslation,
    translatedMessageBody,
    translatedSubject,
    onTranslateMessageClick,
    hideTranslation,
    loading,
  };
};
